
import { User } from "@/types/user";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Edit, FileText, LinkIcon, Linkedin, Twitter } from "lucide-react";

interface ProfileCardProps {
  userProfile: User;
  email: string;
  onEditClick: () => void;
}

const ProfileCard = ({ userProfile, email, onEditClick }: ProfileCardProps) => {
  return (
    <Card className="md:col-span-1">
      <CardHeader className="text-center">
        <div className="flex flex-col items-center">
          <Avatar className="h-24 w-24 mb-4">
            {userProfile.avatar ? (
              <AvatarImage src={userProfile.avatar} alt={userProfile.name} />
            ) : (
              <AvatarFallback className="bg-gradient-to-r from-primary to-primary/80 text-white text-2xl">
                {userProfile.name.charAt(0)}
              </AvatarFallback>
            )}
          </Avatar>
          <CardTitle>{userProfile.name}</CardTitle>
          <CardDescription className="mt-1">
            {email}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col space-y-4">
          <div>
            <span className="text-muted-foreground">Role: </span>
            <span className="font-medium">{userProfile.role}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Institution: </span>
            <span className="font-medium">{userProfile.institution}</span>
          </div>
          {userProfile.publications !== undefined && (
            <div>
              <span className="text-muted-foreground">Publications: </span>
              <span className="font-medium">{userProfile.publications}</span>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Joined: </span>
            <span className="font-medium">
              {userProfile.joinedDate 
                ? new Date(userProfile.joinedDate).toLocaleDateString() 
                : 'Recently'}
            </span>
          </div>
          
          {/* Social Links */}
          {(userProfile.website || userProfile.twitter || userProfile.linkedin || userProfile.orcid) && (
            <>
              <Separator className="my-2" />
              <div className="flex flex-wrap gap-2">
                {userProfile.website && (
                  <a href={userProfile.website} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="icon" className="h-8 w-8" title="Website">
                      <LinkIcon className="h-4 w-4" />
                    </Button>
                  </a>
                )}
                {userProfile.twitter && (
                  <a href={userProfile.twitter} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="icon" className="h-8 w-8" title="Twitter">
                      <Twitter className="h-4 w-4" />
                    </Button>
                  </a>
                )}
                {userProfile.linkedin && (
                  <a href={userProfile.linkedin} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="icon" className="h-8 w-8" title="LinkedIn">
                      <Linkedin className="h-4 w-4" />
                    </Button>
                  </a>
                )}
                {userProfile.orcid && (
                  <a href={userProfile.orcid} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="icon" className="h-8 w-8" title="ORCID">
                      <FileText className="h-4 w-4" />
                    </Button>
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
          onClick={onEditClick}
        >
          <Edit className="h-4 w-4" />
          Edit Profile
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ProfileCard;
